import React, { useMemo, useState } from 'react'
import EquipoCompactRow from './EquipoCompactRow'
import { useTheme } from '../../hooks/useTheme'
import { Form, Row, Col, Button } from 'react-bootstrap'
import { computarGrupoInfoMap } from '../../utils/gruposEquipos'

/**
 * Tabla responsive de equipos con diseño compacto
 * Adapta columnas según breakpoint:
 * - Desktop (>992px): 10 columnas completas
 * - Tablet (768-992px): 8 columnas (oculta Obs, Edit, Cancel)
 * - Móvil (<768px): 5 columnas esenciales
 * 
 * @param {Object} props
 * @param {Array} props.equipos - Array de equipos a mostrar
 * @param {string} props.task - Tipo de tarea ('finalizar' o 'revisar')
 * @param {Object} props.currentUser - Usuario actual
 * @param {Function} props.onEdit - Handler editar equipo
 * @param {Function} props.onCancel - Handler cancelar equipo
 * @param {Function} props.onObserv - Handler ver observación
 * @param {Function} props.onFinalize - Handler finalizar equipo
 * @param {Function} props.onActivate - Handler activar equipo
 * @param {Function} props.onNoFinalize - Handler no finalizar equipo
 * @param {Function} props.onVerDetalle - Handler ver detalle (móvil)
 * @param {Object} props.equiSelectRef - Ref para selección múltiple
 * @param {boolean} props.selectAll - Estado checkbox select all
 * @param {Function} props.onSelectAllChange - Handler select all
 * @param {Function} props.onAprovarOneToggle - Handler toggle individual
 */
const ESTADOS_FILTER = ['No Finalizado', 'Finalizado', 'Pendiente', 'Cancelado', 'Procesado', 'Aprobado', 'Aplazado', 'Entregado'];

const SORT_OPTIONS = [
  { value: '', label: 'Sin orden' },
  { value: 'item_asc', label: 'Ítem A → Z' },
  { value: 'item_desc', label: 'Ítem Z → A' },
  { value: 'consec_asc', label: 'Consecutivo ↑' },
  { value: 'consec_desc', label: 'Consecutivo ↓' },
];

const TableEquiposCompact = ({ 
  equipos = [], 
  task,
  currentUser,
  onEdit,
  onCancel,
  onObserv,
  onFinalize,
  onActivate,
  onNoFinalize,
  onVerDetalle,
  equiSelectRef,
  selectAll,
  onSelectAllChange,
  onAprovarOneToggle,
  onDesaprovar,
  patronIdsActivos = new Set(),
  hayVisitaVigente = true
}) => {
  const { size } = useTheme()
  const screenWidth = size?.width || window.innerWidth

  console.log("Equipos recibidos en TableEquiposCompact:", equipos)
  // Internal filter states
  const [filterItem, setFilterItem] = useState('');
  const [filterMarca, setFilterMarca] = useState('');
  const [filterEstado, setFilterEstado] = useState('');
  const [filterAdicion, setFilterAdicion] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortOrder, setSortOrder] = useState('');
  // '' = todos, 'MINE' = solo del usuario actual, <id> = responsable concreto
  const [filterResponsable, setFilterResponsable] = useState('');


  // Grupos padre↔hijos para mostrar badges en la tabla
  // (spec items-adicionales-equipo-dependiente / visualización).
  // Ver src/utils/gruposEquipos.js para el algoritmo.
  const grupoInfoMap = useMemo(() => computarGrupoInfoMap(equipos), [equipos]);

  // Unique values derived from equipos prop
  const uniqueItems = useMemo(() => {
    const names = equipos.map(e => e.snapshot?.item).filter(Boolean);
    return [...new Set(names)].sort();
  }, [equipos]);

  const uniqueMarcas = useMemo(() => {
    const marcas = equipos.map(e => e.snapshot.marca).filter(Boolean);
    return [...new Set(marcas)].sort();
  }, [equipos]);

  // Responsables únicos presentes en los equipos del listado. Se usa para
  // alimentar el dropdown de filtro; junto con la opción "Mis equipos"
  // (currentUser) permite acotar la vista sin ocupar más espacio que un
  // solo select.
  const uniqueResponsables = useMemo(() => {
    const map = new Map();
    equipos.forEach(eq => {
      const r = eq.responsable;
      if (r && r._id) {
        const id = String(r._id);
        if (!map.has(id)) {
          const label = [r.nombre, r.apellido].filter(Boolean).join(' ').trim()
            || r.correo
            || 'Sin nombre';
          map.set(id, label);
        }
      }
    });
    return Array.from(map, ([id, label]) => ({ id, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [equipos]);

  const currentUserId = currentUser?._id ? String(currentUser._id) : '';
  // Si el usuario actual no tiene equipos propios en el listado, no mostramos
  // "Mis equipos" para no dar la impresión de que filtra algo que no existe.
  const hayEquiposMios = useMemo(
    () => currentUserId && equipos.some(e => String(e.responsable?._id) === currentUserId),
    [equipos, currentUserId]
  );

  // Filtered + sorted list
  const displayEquipos = useMemo(() => {
    const SEARCH_KEYS = ['consecutivo', 'marca', 'modelo', 'serial', 'sede', 'resolucion', 'puntos', 'inventario', 'ubicacion', 'item'];
    let result = equipos;

    if (filterItem) {
      result = result.filter(e => e.snapshot?.item === filterItem);
    }
    if (filterMarca) {
      result = result.filter(e => e.snapshot?.marca === filterMarca);
    }
    if (filterEstado) {
      result = result.filter(e => e.estado === filterEstado);
    }
    if (filterAdicion === 'adicionado') {
      result = result.filter(e => e.adicion === true);
    } else if (filterAdicion === 'original') {
      result = result.filter(e => !e.adicion);
    }

    if (filterResponsable === 'MINE' && currentUserId) {
      result = result.filter(e => String(e.responsable?._id) === currentUserId);
    } else if (filterResponsable && filterResponsable !== 'MINE') {
      result = result.filter(e => String(e.responsable?._id) === filterResponsable);
    }


    if (searchQuery) { // Búsqueda en múltiples campos
      const q = searchQuery.toLowerCase();
      result = result.filter(({ snapshot }) =>
        SEARCH_KEYS.some(k => {
          const val = snapshot?.[k];
          return val?.toString().toLowerCase().includes(q);
        })
      );
    }

    if (sortOrder) {
      result = [...result].sort((a, b) => {
        if (sortOrder === 'item_asc') return (a.snapshot?.item || '').localeCompare(b.snapshot?.item || '');
        if (sortOrder === 'item_desc') return (b.snapshot?.item || '').localeCompare(a.snapshot?.item || '');
        if (sortOrder === 'consec_asc') return (a.consecutivo || 0) - (b.consecutivo || 0);
        if (sortOrder === 'consec_desc') return (b.consecutivo || 0) - (a.consecutivo || 0);
        return 0;
      });
    }

    return result;
  }, [equipos, filterItem, filterMarca, filterEstado, filterAdicion, searchQuery, sortOrder, filterResponsable, currentUserId]);

  const hasActiveFilters = filterItem || filterMarca || filterEstado || filterAdicion || searchQuery || sortOrder || filterResponsable;

  const clearFilters = () => {
    setFilterItem('');
    setFilterMarca('');
    setFilterEstado('');
    setFilterAdicion('');
    setSearchQuery('');
    setSortOrder('');
    setFilterResponsable('');
  };
  
  const handlers = {
    onEdit,
    onCancel,
    onObserv,
    onFinalize,
    onActivate,
    onNoFinalize,
    onVerDetalle,
    onAprovarOneToggle,
    onDesaprovar,
    equiSelectRef
  }
  
  if (!equipos || equipos.length === 0) {
    return (
      <div className="alert alert-info text-center">
        <i className="bi bi-inbox me-2"/>
        No hay equipos disponibles
      </div>
    )
  }
  
  return (
    <div>
      {/* Filter bar */}
      <div className="bg-light border rounded p-2 mb-2">
        <Row className="g-2 align-items-end">
          <Col xs={12} sm={6} md={2}>
            <Form.Select size="sm" value={filterItem} onChange={e => setFilterItem(e.target.value)}>
              <option value="">-- Ítem --</option>
              {uniqueItems.map(item => <option key={item} value={item}>{item}</option>)}
            </Form.Select>
          </Col>
          <Col xs={12} sm={6} md={2}>
            <Form.Select size="sm" value={filterMarca} onChange={e => setFilterMarca(e.target.value)}>
              <option value="">-- Marca --</option>
              {uniqueMarcas.map(m => <option key={m} value={m}>{m}</option>)}
            </Form.Select>
          </Col>
          <Col xs={12} sm={6} md={2}>
            <Form.Select size="sm" value={filterEstado} onChange={e => setFilterEstado(e.target.value)}>
              <option value="">-- Estado --</option>
              {ESTADOS_FILTER.map(est => <option key={est} value={est}>{est}</option>)}
            </Form.Select>
          </Col>
          <Col xs={12} sm={12} md={2}>
            <Form.Control
              size="sm"
              type="text"
              placeholder="Buscar..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
            />
          </Col>
          <Col xs={12} sm={6} md={2}>
            <Form.Select size="sm" value={filterAdicion} onChange={e => setFilterAdicion(e.target.value)}>
              <option value="">-- Adición --</option>
              <option value="adicionado">Solo adicionados</option>
              <option value="original">Solo originales</option>
            </Form.Select>
          </Col>
          <Col xs={12} sm={6} md={2}>
            <Form.Select size="sm" value={sortOrder} onChange={e => setSortOrder(e.target.value)}>
              {SORT_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
            </Form.Select>
          </Col>
          {/* Filtro por responsable: un único select que combina "Mis equipos"
              (atajo para currentUser) + lista de responsables únicos del
              listado. Compacto y versátil — no agrega una fila extra en
              desktop porque usa md={2} como los demás. */}
          <Col xs={12} sm={6} md={2}>
            <Form.Select
              size="sm"
              value={filterResponsable}
              onChange={e => setFilterResponsable(e.target.value)}
              title="Filtrar por responsable"
            >
              <option value="">-- Responsable --</option>
              {hayEquiposMios && <option value="MINE">👤 Mis equipos</option>}
              {uniqueResponsables.length > 0 && (
                <option disabled>──────────</option>
              )}
              {uniqueResponsables.map(r => (
                <option key={r.id} value={r.id}>{r.label}</option>
              ))}
            </Form.Select>
          </Col>
          {hasActiveFilters && (
            <Col xs="auto">
              <Button size="sm" variant="outline-secondary" onClick={clearFilters}>
                <i className="bi bi-x-circle me-1"/>Limpiar
              </Button>
            </Col>
          )}
        </Row>
        {hasActiveFilters && (
          <small className="text-muted mt-1 d-block">
            Mostrando <strong>{displayEquipos.length}</strong> de <strong>{equipos.length}</strong> equipos
          </small>
        )}
      </div>

    <div className="table-responsive">
      <table className="table table-sm table-hover table-bordered align-middle mb-0"
        style={{fontSize: "8px"}}
      >
        <thead className="table-info">
          <tr>
            {/* Índice */}
            <th className="text-center" style={{ width: '50px' }}>#</th>

            {/* Adición + Grupo dependencia (tablet+): columna compacta, layout vertical */}
            {screenWidth >= 768 && (
              <th
                className="text-center"
                style={{ width: '36px', minWidth: '36px', maxWidth: '36px', padding: '2px' }}
                title="Ingresado al laboratorio · Grupo padre/dependiente · Equipo adicionado"
              >
                <i className="bi bi-diagram-3" style={{ fontSize: '0.8rem' }} />
              </th>
            )}
            
            {/* Item */}
            <th style={{ minWidth: '140px' }}>Item</th>
            
            {/* Equipos */}
            <th style={{ minWidth: '170px' }}>Equipos</th>
            
            {/* Ubicación (tablet+) */}
            {screenWidth >= 768 && (
              <th style={{ minWidth: '150px' }}>Ubicación</th>
            )}
            
            {/* Puntos (tablet+) */}
            {screenWidth >= 768 && (
              <th className="text-center" style={{ width: '80px' }}>Puntos</th>
            )}
              
            {/* Consecutivo (tablet+) */}
            <th className="text-center" style={{ width: '60px' }}>Consec.</th>

            
            {/* Estado */}
            <th className="text-center" style={{ width: '90px' }}>Estado</th>

            {/* Pat. (tablet+, solo task=finalizar) */}
            {screenWidth >= 768 && task === 'finalizar' && (
              <th className="text-center" style={{ width: '50px' }} title="Cobertura de patrones">Pat.</th>
            )}
            
            {/* Responsable (desktop) */}
            {screenWidth >= 992 && (
              <th style={{ minWidth: '140px' }}>Responsable</th>
            )}
            
            {/* Observación (desktop) */}
            {screenWidth >= 992 && (
              <th className="text-center" style={{ width: '50px' }}>
                <i className="bi bi-eye" title="Observación"/>
              </th>
            )}
            
            {/* Acción */}
            <th className="text-center" style={{ width: screenWidth >= 992 ? '80px' : '60px' }}>
              {task === 'revisar' ? (
                <input
                  type="checkbox"
                  checked={selectAll}
                  onChange={onSelectAllChange}
                  title="Seleccionar todos"
                />
              ) : (
                'Acción'
              )}
            </th>
            
            {/* Editar (desktop, no en revisar) */}
            {screenWidth >= 992 && task !== 'revisar' && (
              <th className="text-center" style={{ width: '60px' }}>
                <i className="bi bi-pencil" title="Editar"/>
              </th>
            )}
            
            {/* Cancelar (desktop, no en revisar) */}
            {screenWidth >= 992 && task !== 'revisar' && (
              <th className="text-center" style={{ width: '60px' }}>
                <i className="bi bi-x-circle" title="Cancelar"/>
              </th>
            )}
          </tr>
        </thead>
        
        <tbody>
          {displayEquipos.map((eq, i) => (
            <EquipoCompactRow
              key={eq._id || i}
              equipo={eq}
              index={i}
              task={task}
              screenWidth={screenWidth}
              currentUser={currentUser}
              handlers={handlers}
              patronIdsActivos={patronIdsActivos}
              hayVisitaVigente={hayVisitaVigente}
              grupoInfo={grupoInfoMap.get(String(eq._id))}
            />
          ))}
        </tbody>
      </table>
      
      {/* Footer con contador */}
      <div className="d-flex justify-content-between align-items-center p-2 bg-light border-top">
        <small className="text-muted">
          <i className="bi bi-list-ul me-1"/>
          Total: <strong>{displayEquipos.length}</strong>{hasActiveFilters && ` / ${equipos.length}`} equipos
        </small>
        
        {task === 'revisar' && equiSelectRef?.current && (
          <small className="text-primary">
            <i className="bi bi-check-square me-1"/>
            Seleccionados: <strong>{equiSelectRef.current.length}</strong>
          </small>
        )}
      </div>
    </div>
    </div>
  )
}

export default TableEquiposCompact
